import type { SelectOption } from '@/components';
import type { Industry, TenantType } from '@/types';
import type { FieldOption } from '@/components/AdminSearchWithFieldSelect';

/** Field options for the super-admin Registered Users / Subscription search dropdowns. */
export const TENANT_SEARCH_FIELD_OPTIONS: FieldOption[] = [
  { value: 'name',  label: 'Name' },
  { value: 'gst',   label: 'GST Number' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'code',  label: 'Sl Number' },
];

/** Field options for the super-admin Vendors search dropdown. */
export const VENDOR_SEARCH_FIELD_OPTIONS: FieldOption[] = [
  { value: 'name',  label: 'Name' },
  { value: 'city',  label: 'City' },
  { value: 'gst',   label: 'GST Number' },
  { value: 'phone', label: 'Phone Number' },
];

/**
 * The three customer-facing business types.
 * ADMIN is intentionally excluded — it's reserved for the platform-level
 * super-admin tenant and should never appear in the registration UI.
 */
export const BUSINESS_TYPE_OPTIONS: ReadonlyArray<SelectOption & { value: TenantType }> = [
  { value: 'WHOLESALER', label: 'Wholesaler / Distributor' },
  { value: 'RETAILER', label: 'Retailer' },
  { value: 'REPRESENTATIVE', label: 'Representative' },
];

/**
 * Login options shown on the regular /login page. Same three customer-facing
 * types as registration — OTHERS (the platform-level Ardinotech admin
 * tenant) is intentionally absent here too. It's not a selectable option
 * anywhere in the UI; the super-admin signs in via a separate, unlinked
 * route (/super-admin-login) that must be typed in directly, with the
 * business type fixed rather than offered as a dropdown choice.
 */
export const BUSINESS_TYPE_LOGIN_OPTIONS: ReadonlyArray<SelectOption & { value: TenantType }> = [
  { value: 'WHOLESALER', label: 'Wholesaler / Distributor' },
  { value: 'RETAILER', label: 'Retailer' },
  { value: 'REPRESENTATIVE', label: 'Representative' },
];

/** Industry / line of business — the same list applies to all tenant types. */
export const INDUSTRY_OPTIONS: ReadonlyArray<SelectOption & { value: Industry }> = [
  { value: 'PHARMACEUTICAL', label: 'Pharmaceutical' },
  { value: 'FMCG', label: 'FMCG' },
  { value: 'ELECTRONICS', label: 'Electronics' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'FOOD_BEVERAGE', label: 'Food & Beverage' },
  { value: 'HARDWARE', label: 'Hardware' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'AUTOMOTIVE', label: 'Automotive' },
  { value: 'OTHER', label: 'Other' },
];

/** Accounting / ERP software options — stored as the value string in the DB. */
export const VENDOR_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'NONE',    label: 'None' },
  { value: 'TALLY',   label: 'Tally' },
  { value: 'BUSY',    label: 'Busy' },
  { value: 'MARG',    label: 'Marg ERP' },
  { value: 'SAP',     label: 'SAP' },
  { value: 'MIRACLE', label: 'Miracle' },
  { value: 'VYAPAR',  label: 'Vyapar' },
  { value: 'OTHERS',  label: 'Others' },
];

/** Display label for a vendor value. */
export function vendorLabel(value?: string | null): string {
  if (!value || value === 'NONE') return 'None';
  return VENDOR_OPTIONS.find(o => o.value === value)?.label ?? value;
}
